import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ColorScheme = "dark" | "light" | "system";
export type ResolvedScheme = "dark" | "light";

export interface ThemeContextValue {
  scheme: ColorScheme;
  resolvedScheme: ResolvedScheme;
  accessible: boolean;
  setScheme: (scheme: ColorScheme) => void;
  setAccessible: (value: boolean) => void;
  toggleScheme: () => void;
  toggleAccessible: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const SCHEME_STORAGE_KEY = "wc-theme";
const ACCESSIBLE_STORAGE_KEY = "wc-accessible";

function readStoredScheme(defaultScheme: ColorScheme): ColorScheme {
  if (typeof window === "undefined") {
    return defaultScheme;
  }
  const stored = window.localStorage.getItem(SCHEME_STORAGE_KEY);
  if (stored === "dark" || stored === "light" || stored === "system") {
    return stored;
  }
  return defaultScheme;
}

function readStoredAccessible(defaultValue: boolean): boolean {
  if (typeof window === "undefined") {
    return defaultValue;
  }
  const stored = window.localStorage.getItem(ACCESSIBLE_STORAGE_KEY);
  if (stored === "true") {
    return true;
  }
  if (stored === "false") {
    return false;
  }
  return defaultValue;
}

function resolveScheme(scheme: ColorScheme): ResolvedScheme {
  if (scheme !== "system") {
    return scheme;
  }
  if (typeof window === "undefined" || !window.matchMedia) {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function applyBodyClasses(resolved: ResolvedScheme, accessible: boolean) {
  if (typeof document === "undefined") {
    return;
  }
  const body = document.body;
  if (!body) {
    return;
  }
  body.classList.toggle("light-theme", resolved === "light");
  body.classList.toggle("accessible", accessible);
  document.documentElement.style.colorScheme = resolved;
}

export interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ColorScheme;
  defaultAccessible?: boolean;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultAccessible = false,
}: ThemeProviderProps) {
  const [scheme, setSchemeState] = useState<ColorScheme>(defaultTheme);
  const [accessible, setAccessibleState] = useState<boolean>(defaultAccessible);
  const [resolvedScheme, setResolvedScheme] = useState<ResolvedScheme>(() =>
    defaultTheme === "light" ? "light" : "dark",
  );

  useEffect(() => {
    const storedScheme = readStoredScheme(defaultTheme);
    const storedAccessible = readStoredAccessible(defaultAccessible);
    setSchemeState(storedScheme);
    setAccessibleState(storedAccessible);
    const resolved = resolveScheme(storedScheme);
    setResolvedScheme(resolved);
    applyBodyClasses(resolved, storedAccessible);
  }, [defaultTheme, defaultAccessible]);

  useEffect(() => {
    const resolved = resolveScheme(scheme);
    setResolvedScheme(resolved);
    applyBodyClasses(resolved, accessible);
  }, [scheme, accessible]);

  useEffect(() => {
    if (scheme !== "system" || typeof window === "undefined") {
      return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const listener = () => {
      const next = resolveScheme("system");
      setResolvedScheme(next);
      applyBodyClasses(next, accessible);
    };
    mq.addEventListener("change", listener);
    return () => {
      mq.removeEventListener("change", listener);
    };
  }, [scheme, accessible]);

  const setScheme = useCallback((next: ColorScheme) => {
    setSchemeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SCHEME_STORAGE_KEY, next);
    }
  }, []);

  const setAccessible = useCallback((value: boolean) => {
    setAccessibleState(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        ACCESSIBLE_STORAGE_KEY,
        value ? "true" : "false",
      );
    }
  }, []);

  const toggleScheme = useCallback(() => {
    setSchemeState((prev) => {
      const next: ColorScheme =
        prev === "dark" ? "light" : prev === "light" ? "dark" : "light";
      if (typeof window !== "undefined") {
        window.localStorage.setItem(SCHEME_STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  const toggleAccessible = useCallback(() => {
    setAccessibleState((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          ACCESSIBLE_STORAGE_KEY,
          next ? "true" : "false",
        );
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      scheme,
      resolvedScheme,
      accessible,
      setScheme,
      setAccessible,
      toggleScheme,
      toggleAccessible,
    }),
    [
      scheme,
      resolvedScheme,
      accessible,
      setScheme,
      setAccessible,
      toggleScheme,
      toggleAccessible,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}
